import { datadogRum } from "@datadog/browser-rum";
import type {
  LegacyTelemetryUser,
  TelemetryAdapter,
  TelemetryAttributes,
  TelemetryLogLevel,
  TelemetryUser,
} from "../types";

/**
 * The web Datadog adapter (`@datadog/browser-rum`), clinic app and patient
 * portal alike.
 *
 * A LEAF, like its native twin: it imports nothing else from the app, because
 * `Common/Utils/logger` reaches it through the facade. Init lives in
 * `init.web.ts`, which only `boot.ts` imports. Every argument below is what the
 * replaced call sites passed, unchanged. Each returns what the SDK returned,
 * so the facade's guard can contain a rejection as well as a throw.
 */

const addAction = (name: string, attributes: TelemetryAttributes) =>
  datadogRum.addAction(name, attributes);

const addError = (message: string, attributes: TelemetryAttributes) =>
  datadogRum.addError(new Error(message), attributes);

const captureException = (error: Error | string, message?: string) => {
  const isStringError = typeof error === "string";
  const asError = isStringError ? new Error(error) : error;
  const finalMessage = message ?? (isStringError ? error : error.message);
  return datadogRum.addError(asError, { message: finalMessage });
};

const setUser = ({ id, userType, tenantId }: TelemetryUser) => {
  // `setUser` replaces the whole user, so a later ids-only call also clears
  // a legacy name, email and clinic name.
  return datadogRum.setUser({
    id,
    ...(userType ? { userType } : {}),
    ...(tenantId ? { tenantId } : {}),
  });
};

const setUserLegacy = ({
  id,
  name,
  email,
  userType,
  tenantName,
}: LegacyTelemetryUser) =>
  datadogRum.setUser({
    id,
    ...(name ? { name } : {}),
    ...(email ? { email } : {}),
    ...(userType ? { userType } : {}),
    ...(tenantName ? { tenantName } : {}),
  });

// Manual views are native only; on web these were always no-ops.
const startView = (
  _key: string,
  _name: string,
  _attributes?: TelemetryAttributes,
) => {};

const stopView = (_key: string) => {};

const addFeatureFlagEvaluation = (flagKey: string, value: unknown) =>
  datadogRum.addFeatureFlagEvaluation(flagKey, value);

/**
 * Web has no Datadog Logs SDK, so log lines go to the console only, which is
 * what browser RUM already captures (DHT-5565).
 */
const log = (level: TelemetryLogLevel, message: string) => {
  switch (level) {
    case "error":
      // eslint-disable-next-line no-console
      console.error(message);
      break;
    case "warn":
      // eslint-disable-next-line no-console
      console.warn(message);
      break;
    case "info":
      // eslint-disable-next-line no-console
      console.info(message);
      break;
    case "debug":
    default:
      // eslint-disable-next-line no-console
      console.debug(message);
      break;
  }
};

export const datadogAdapter: TelemetryAdapter = {
  addAction,
  addError,
  captureException,
  setUser,
  setUserLegacy,
  startView,
  stopView,
  addFeatureFlagEvaluation,
  log,
};
